import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const simulateBidsBodySchema = z.object({
  advertisement_id: z.number(),
  parameters: z
    .object({
      algorithm: z.string().optional(),
      guardrails: z
        .object({
          min_bid: z.number(),
          max_bid: z.number(),
        })
        .optional(),
      max_acos: z.number().optional(),
      min_acos: z.number().optional(),
      daily_budget: z.number().optional(),
      targets_dict: z.record(z.string(), z.any()).default({}),
    })
    .optional(),
});

const simulationRowBaseSchema = z.object({
  target_id: z.coerce.string(),
  current_bid: z.number(),
  new_bid: z.number(),
  bid_delta: z.number(),
  est_cpc: z.number(),
  est_impr: z.number(),
  est_spend: z.number(),
  est_sales: z.number(),
  est_acos: z.number(),
  placement_multiplier: z.record(z.string(), z.number()),
});

const keywordSimulationRowSchema = simulationRowBaseSchema.extend({
  type: z.literal("keyword"),
  text: z.string(),
  match_type: z.string(),
});

const targetSimulationRowSchema = simulationRowBaseSchema.extend({
  type: z.literal("target"),
  target_targeting_name: z.string(),
  text: z.null(),
  match_type: z.null(),
});

const simulationRowSchema = z.discriminatedUnion("type", [keywordSimulationRowSchema, targetSimulationRowSchema]);

const simulationResultSchema = z
  .object({
    columns: z.array(z.string()),
    rows: z.array(z.array(z.unknown())),
  })
  .transform((raw) =>
    raw.rows.map((row) => simulationRowSchema.parse(Object.fromEntries(raw.columns.map((col, i) => [col, row[i]])))),
  );

const simulateBidsResponseSchema = z.object({ simulation_id: z.string() });

const bidSimulationStatusSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("running") }),
  z.object({ status: z.literal("done"), result: simulationResultSchema }),
  z.object({ status: z.literal("failed"), error: z.string().optional() }),
]);

const deployOverrideSchema = z.object({
  target_id: z.string(),
  type: z.string(),
  new_bid: z.number(),
});

const deployBidsBodySchema = z.object({
  advertisement_id: z.number(),
  overrides: z.array(deployOverrideSchema),
});

const deployBidsResponseSchema = z.object({
  status: z.string(),
});

export type SimulationRow = z.infer<typeof simulationRowSchema>;

export const bidSimulationApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "simulateBids",
    method: "post",
    path: "/simulate_bids/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: simulateBidsBodySchema,
      },
    ],
    status: 202,
    response: simulateBidsResponseSchema,
  })
    .addEndpoint({
      alias: "getBidSimulation",
      method: "get",
      path: "/simulate_bids/:simulation_id/",
      status: 200,
      response: bidSimulationStatusSchema,
    })
    .addEndpoint({
      alias: "deploySimulatedBids",
      method: "post",
      path: "/deploy_simulated_bids/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: deployBidsBodySchema,
        },
      ],
      status: 200,
      response: deployBidsResponseSchema,
    })
    .build(),
);
